<script>
  import { _ } from "svelte-i18n"
  import { fade } from 'svelte/transition'
  import { cubicOut } from 'svelte/easing'
  import { derived } from 'svelte/store';
  import { filter, sortBy, shuffle, isEmpty, uniq, size } from 'lodash'
  import { resetAllFilters } from '../modules/helpers'
  import { setPreviewMode } from "./TextArea.svelte"
  import { 
    detected_fonts, previews_are_google_fonts, hero_expanded, previews_font_size,
    previews_tags_filter_operator, flash_content, google_fonts, previews_are_zoomed, font_detection_in_progress,
    previews_nav_filters, previews_search_filter, previews_weight_filter, previews_tags_filter, dark_theme, previews_sort_order, previews_tracking, previews_leading, 
    text_previewed, text_entered, text_caseapplied, selected_text_case, text_entered_once, user, previews_categories_filter, dropped_font_files,
    previews_storefronts_filter, user_storefronts, previews_are_highlighted, user_valid, hobby_font_limit, user_installed_fonts_count,
    can_upgrade_from_pro_to_business, user_uploaded_fonts_loading_state
  } from './../stores.js'
  import { loadUserContentData } from "../services/user";
  import Inview from 'svelte-inview'
  import FontsNav from './FontsNav.svelte'
  import Preview from './Preview.svelte'
  import PreviewsTags from './PreviewsTags.svelte'
  import StorefrontsIndex from "./StorefrontsIndex.svelte"
  import PreviewsEmptyStateController from './PreviewsEmptyStateController.svelte'
  import PreviewsStickyButtons from './PreviewsStickyButtons.svelte'
  import PreviewsViewOptions from './PreviewsViewOptions.svelte'
  import ModalExportPNG from './modals/ModalExportPNG.svelte'
  import ModalPrint from './modals/ModalPrint.svelte'
  import PreviewsScrollObserver from './PreviewsScrollObserver.svelte'
  import PreviewsScrollToTopButton from './PreviewsScrollToTopButton.svelte'
  import DefaultPreviewWidth from './DefaultPreviewWidth.svelte'
  import ModalBuy from './modals/ModalBuy2.svelte'
  import AutoScrollController from './AutoScrollController.svelte'
  import Button from '../elements/Button.svelte'
  import InsertManager from "./InsertManager.svelte"
  import ModalManageSavedSelections from './modals/ModalManageSavedSelections.svelte'
  import Dropzone from './Dropzone.svelte'
  import StorefrontFooter from "../content/StorefrontFooter.svelte"
  import StorefrontURLBar from "./StorefrontURLBar.svelte"
  import StorefrontTextfield from "./StorefrontTextfield.svelte"
  import StorefrontDescription from "./StorefrontDescription.svelte"
  import StorefrontName from "./StorefrontName.svelte"
  import FontLimitInsert from "../content/inserts/FontLimit.svelte"
  import BusinessUpsellNotice from "./BusinessUpsellNotice.svelte";
  import LoadingFontsState from "../content/LoadingFontsState.svelte";
  import Blink from '../elements/Blink.svelte'

  let previews_element
  let remembered_selections = []

  let prepare_for_export = false
  let export_without_bg = false
  let export_without_fontname = false
  let export_with_inverted_theme = false
  
  let shuffled_fonts
  let previous_selected_count = 0
  let filtered_preview_count
  let empty_filter_result = false
  
  let pagination_page = 1
  let pagination_batch_size = 40
  let pagination_scrollmark_el

  let loaded_all_previews = false
  let uploading = false
    
  let autoScrollComponent

  let exceeded_hobby_font_limit = false;
  // LIMIT FONTS
  const previews = derived(
    [detected_fonts, previews_are_google_fonts, google_fonts, user, user_valid, hobby_font_limit],
    ([$detected_fonts, $previews_are_google_fonts, $google_fonts, $user, $user_valid, $hobby_font_limit]) => {
      let fonts = $previews_are_google_fonts ? $google_fonts : $detected_fonts;
      
      // Limit only user fonts if user is not logged in or not valid
      if (!$user || !$user_valid) {
        // Split fonts into user and system fonts
        const userFonts = fonts?.filter(f => f.user === true && (!f.uploaded || !f.dropped));
        const systemFonts = fonts?.filter(f => !f.user);

        // Set the count of user installed fonts
        $user_installed_fonts_count = userFonts?.length;
        
        // Apply limit only to user fonts
        const limitedUserFonts = userFonts?.slice(0, $hobby_font_limit);
        
        // Combine limited user fonts with all system fonts
        fonts = [...(systemFonts || []), ...(limitedUserFonts || [])];

        if (limitedUserFonts?.length < userFonts?.length) {
          exceeded_hobby_font_limit = true;
          console.log('-- Previews - limiting previews, showing ' + limitedUserFonts?.length + ' of ' + userFonts?.length + ' user installed fonts');
        }
      }
      
      return fonts;
    }
  );
  // Show font limit insert if user is not valid and is showing user or all fonts
  $: show_font_limit_insert = 
    $previews_tags_filter.length === 0 
    && !$previews_search_filter 
    && !$user_valid
    && ($previews_nav_filters.user || isEmpty($previews_nav_filters))
    && $user_installed_fonts_count > $hobby_font_limit

  // SELECTIONS
  $: selected_fonts = filter($previews, 'selected')
  $: selected_count = selected_fonts.length
  $: selected_count && showSelectionFlashMessage()
  $: {
    if (selected_count === 0 && $previews_nav_filters.selected) {
      previews_nav_filters.showAll()
    }
  }

  // Selected Storefront
  $: selectedStorefront = derived(
    [previews_storefronts_filter, user_storefronts],
    ([$previews_storefronts_filter, $user_storefronts]) => {
      if ($previews_storefronts_filter) {
        const storefront = $user_storefronts.find(storefront => storefront.id == $previews_storefronts_filter);
        // Add _originalDescription property to track changes
        if (storefront && !storefront._originalDescription) {
          storefront._originalDescription = storefront.description;
        }
        return storefront;
      }
      return null;
    }
  )

  // Handle storefront filter changes
  $: {
    if ($previews_nav_filters.storefront && selected_count > 0) {
      // Remember currently selected fonts and deselect them
      remembered_selections = selected_fonts.map(f => f.id);
      deselectAllFonts(true);
      console.log('Storefronts - Deselecting and remembering selection')
    } else if (remembered_selections.length > 0) {
      // Restore selections when leaving storefront view
      $previews.forEach((font) => {
        if (remembered_selections.includes(font.id)) {
          font.selected = true;
          updateFont(font);
        }
      });
      console.log('Storefronts - Restored remembered selection')
      selected_count = remembered_selections.length;
      remembered_selections = [];
    }
  }

  // FILTERING
  $: if ($previews_nav_filters) 					console.log('Filters - previews_nav_filters changed: ' + JSON.stringify($previews_nav_filters))
  $: if ($previews_search_filter) 				console.log('Filters - previews_search_filter changed: ' + $previews_search_filter)
  $: if ($previews_weight_filter) 				console.log('Filters - previews_weight_filter changed: ' + $previews_weight_filter)
  $: if ($previews_tags_filter_operator) 	console.log('Filters - previews_tags_filter_operator changed: ' + $previews_tags_filter_operator)
  $: if ($previews_tags_filter) {
    if ($previews_tags_filter.length > 0) {
      console.log('Filters - previews_tags_filter changed: ' + $previews_tags_filter)
      if (typeof window.gtag !== "undefined") {
        gtag('event', 'tag_filter_applied');
      }
    }
  }
  $: if ($previews_categories_filter) {
    if ($previews_categories_filter.length > 0) {
      console.log('Filters - previews_categories_filter changed: ' + $previews_categories_filter)
    }
  }
  $: if ($previews_storefronts_filter) {
    console.log('Filters - previews_storefronts_filter changed: ' + $previews_storefronts_filter)
  }
  // $: if (filtered_previews) console.log('Previews - filtered previews updated: ' + filtered_previews.length)
  
  function filterPreviews(fonts, nav_filters, search_filter, weight_filter, tags_filter, tags_filter_operator, categories_filter, storefront_filter) {
    // console.log('│┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅')
    // console.log('│ filtering: '+ JSON.stringify(nav_filters))
    // console.log('│ searching: '+ search_filter)
    // console.log('│ weight   : '+ weight_filter)
    // console.log('│ tags     : '+ tags_filter)
    // console.log('│ tags op. : '+ tags_filter_operator)
    // console.log('│ categories: '+ categories_filter)
    // console.log('│ storefront: ', storefront_filter)
    // console.log('┕━━━━━━━━━━━━━━━━━━━━━━━━━')
    let filtered = fonts

    // Tag filter, only effective under Tagged fonts or Google Fonts not Selection Filtered
    if (tags_filter.length > 0 && (nav_filters.tagged || ($previews_are_google_fonts && !nav_filters.selected))) {
      if (tags_filter_operator === 'and') { tags_filter.forEach(tag => filtered = filter( filtered.filter(f => f.tags), (f) => f.tags.includes(tag))) }
      if (tags_filter_operator === 'or') {
        let filtered_with_or = []
        tags_filter.forEach((tag, i) => filtered_with_or[i] = filter( filtered.filter(f => f.tags), (f) => f.tags.includes(tag)))
        filtered = uniq(filtered_with_or.flat())
      }
    }

    // Categories filter, only effective under Your fonts
    if (categories_filter.length > 0 && !nav_filters.selected) {
      if (tags_filter_operator === 'and') { categories_filter.forEach(filter => filtered = filter( filtered.filter(f => f.category), (f) => f.category === filter)) }
      if (tags_filter_operator === 'or') {
        let filtered_with_or = []
        categories_filter.forEach((category, i) => filtered_with_or[i] = filter( filtered.filter(f => f.category), (f) => f.category === category))
        filtered = uniq(filtered_with_or.flat())
      }
    }

    // Storefront filter only when storefront is selected from nav
    if (storefront_filter && nav_filters.storefront) {
      filtered = filter(filtered, (f) => f.storefronts && f.storefronts.some(storefront => storefront.id === storefront_filter));
    }


    // Search filter, not effective on first character and when selection is filtered
    if (search_filter.length > 1 && !nav_filters.selected) { filtered = filter(filtered, (f) => f.display_name.toLowerCase().includes($previews_search_filter.toLowerCase())) }
    
    // Weight filter, not effective when selection is filtered
    if (weight_filter !== '' && !nav_filters.selected) { filtered = filter(filtered, (f) => f.weight === weight_filter) }
    if (!isEmpty(nav_filters)) { filtered = filter(filtered, nav_filters) }
    if (isEmpty(filtered)) { empty_filter_result = true } else { empty_filter_result = false }
    filtered_preview_count = size(filtered)
    return filtered
  }
  $: filter_params = [$previews_nav_filters, $previews_search_filter, $previews_weight_filter, $previews_tags_filter, $previews_tags_filter_operator, $previews_categories_filter, $previews_storefronts_filter]
  $: filtered_previews = sortPreviews(filterPreviews($previews, ...filter_params), $previews_sort_order)
  
  // PAGINATION
  $: paginated_previews = filtered_previews.slice(0, pagination_page * pagination_batch_size)
  $: console.log('Previews - loaded ' + pagination_page * pagination_batch_size + ' previews')

  // SORTING
  function sortPreviews(fonts, direction) {
    if (direction === 'random') {
      return shuffleFonts(fonts);
    } else if (direction === 'descending') {
      // Sort by family_name (descending, case-insensitive), weight (undefined = 400, ascending), and display_name (descending)
      return sortBy(fonts, [
        f => f && f.family_name ? f.family_name.toLowerCase() : '',
        f => f && f.weight !== undefined ? -f.weight : 400,
        f => f && f.display_name ? f.display_name.toLowerCase() : ''
      ]).reverse();
    } else {
      // Sort by family_name (ascending, case-insensitive), weight (undefined = 400, ascending), and display_name (ascending)
      return sortBy(fonts, [
        f => f && f.family_name ? f.family_name.toLowerCase() : '',
        f => f && f.weight !== undefined ? f.weight : 400,
        f => f && f.display_name ? f.display_name.toLowerCase() : ''
      ]);
    }
  }

  
  function shuffleFonts(fonts) {
    if (!shuffled_fonts || shuffled_fonts.length !== fonts.length ) {
      shuffled_fonts = shuffle(fonts) 
    }
    return shuffled_fonts
  }

  // SELECTING
  export function deselectAllFonts(silent = false) { 
    $previews.forEach((font) => { font.selected = false; updateFont(font);})
    if (!silent) $flash_content = $_('flashes.deselectedAll')
    selected_count = 0
    previous_selected_count = 0
  }

  export function selectAllFonts() { 
    filtered_previews.forEach((font) => {font.selected = true; updateFont(font)})
  }

  function loadSelection(event) {
    // event.detail is the selection object to load
    const saved_selection = event.detail
    
    // get fonts with selections and then get the ones with the selected id
    let fonts_to_select = $detected_fonts.filter(f => f.selections).filter(f => f.selections.includes(saved_selection.id))

    // select those fonts
    fonts_to_select.forEach((font) => {font.selected = true; updateFont(font)})

    // load saved properties
    $previews_font_size = saved_selection.font_scale
    $previews_tracking = saved_selection.tracking
    $previews_leading = saved_selection.line_space
    $text_entered = saved_selection.wordmark
    $text_previewed = saved_selection.wordmark
    $text_caseapplied = saved_selection.wordmark
    $previews_sort_order = saved_selection.sort_order
    $selected_text_case = saved_selection.char_case
    // simulate text entry from cover
    $hero_expanded = false
    $text_entered_once = true
    setPreviewMode()
    // filter selection
    previews_nav_filters.filterSelected()
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth'})
    console.log('User - Loaded saved selection')
  }

  function showSelectionFlashMessage() {
    if (previous_selected_count < selected_count) {
      if (selected_count === 1) {
        $flash_content = $_('flashes.selectionSingular')
      }
      else {
        $flash_content = $_('flashes.selectionPlural', { values: { selection_count: selected_count } } )
      }
    } 
    else {
      // TODO show_deselect has no use and should be removed
      $flash_content = { message: $_('flashes.deselected'), show_deselect: true }
    }
    previous_selected_count = selected_count
  }
  
  // UPDATING
  // Expects a font object with updated properties directly or within an event.detail object
  function updateFont(event) {
    const font = event.detail || event
    
    // Update the appropriate source store
    if ($previews_are_google_fonts) {
      google_fonts.update(fonts => {
        const i = fonts.findIndex(f => f.id === font.id);
        if (i >= 0) {
          const newFonts = [...fonts];
          newFonts[i] = { ...newFonts[i], ...font };
          return newFonts;
        }
        return fonts;
      });
    } else {
      detected_fonts.update(fonts => {
        const i = fonts.findIndex(f => f.id === font.id);
        if (i >= 0) {
          const newFonts = [...fonts];
          newFonts[i] = { ...newFonts[i], ...font };
          return newFonts;
        }
        return fonts;
      });
    }
  }

  // PAGINATION
  function loadNextBatch() {
    if (pagination_page * pagination_batch_size < size(filtered_previews)) {
      pagination_page++
    } else {
      loaded_all_previews = true
    }
  }
  
  // GOOGLE FONTS
  async function closeGoogleFonts() {
    console.log('Google Fonts - off')
    resetAllFilters()
    $previews_are_google_fonts = false
  }

  async function toggleGoogleFonts() {
    console.log('Google Fonts - toggle')
    $previews_are_google_fonts = !$previews_are_google_fonts
    resetAllFilters()
  }

  // REMOVE DROPPED FONTS
  function removeDroppedFonts() {
    console.log('Dropped Fonts - remove dropped fonts')
    $detected_fonts = filter($detected_fonts, (f) => !f.dropped)
    $dropped_font_files = []
  }
  
  // RANDOM INSERTS
  import Inserts_NightTheme from '../content/inserts/NightTheme.svelte'
  import Inserts_ExportImage from '../content/inserts/ExportImage.svelte'
  import Inserts_SupportWordmark from '../content/inserts/SupportWordmark.svelte'
  import Inserts_RandomOrder from '../content/inserts/RandomOrder.svelte'
  import Inserts_DisableAds from '../content/inserts/DisableAds.svelte'
  import Inserts_CharacterHighlighting from '../content/inserts/CharacterHighlighting.svelte'
  import Inserts_AutoScroll from '../content/inserts/AutoScroll.svelte'
  const inserts_day = [
    Inserts_DisableAds, 
    Inserts_SupportWordmark, 
    Inserts_RandomOrder, 
    Inserts_CharacterHighlighting, 
    Inserts_AutoScroll, 
    Inserts_ExportImage, 
  ]

  const inserts_night = [
    Inserts_NightTheme, 
    Inserts_SupportWordmark, 
    Inserts_AutoScroll, 
  ]

  const now = new Date(Date.now()).getHours()
  const random_day_insert = inserts_day[Math.floor(Math.random()*inserts_day.length)]
  const random_night_insert = inserts_night[Math.floor(Math.random()*inserts_night.length)]
  const random_insert = (now >= 6 && now <= 19) ? random_day_insert : random_night_insert

  const handleStorefrontTextfieldTextChange = (event) => {
    $text_entered = event.detail
		$text_previewed = event.detail
		$text_caseapplied = event.detail
		$previews_are_highlighted = false			// reset highlighting
		$selected_text_case = undefined				// reset case selection
		setPreviewMode()
  }

  const handleStorefrontDescriptionChange = (event) => {
    if ($previews_storefronts_filter) {
      // Create a new array with the updated storefront
      const updatedStorefronts = $user_storefronts.map(storefront => {
        if (storefront.id == $previews_storefronts_filter) {
          return { ...storefront, description: event.detail };
        }
        return storefront;
      });
      
      // Update the store with the new array
      user_storefronts.set(updatedStorefronts);
    }
  }

  const handleStorefrontNameChange = (event) => {
    if ($previews_storefronts_filter) {
      // Create a new array with the updated storefront
      const updatedStorefronts = $user_storefronts.map(storefront => {
        if (storefront.id == $previews_storefronts_filter) {
          return { ...storefront, name: event.detail };
        }
        return storefront;
      });
      
      // Update the store with the new array  
      user_storefronts.set(updatedStorefronts);
    }
  }

  const handleUploading = (event) => {
    uploading = event.detail
  }

  const handleReloadFonts = () => {
    // show loading state
    user_uploaded_fonts_loading_state.setLoading()
    // wait 1 second to show that actually trying to load fonts
    setTimeout(() => {
      // Need to reload all user content data to be applied to uploaded fonts
      loadUserContentData()
    }, 1000)
  } 

</script>

{#if !$hero_expanded}
  <div 
  class="previewsContainer" 
  transition:fade="{{ duration: 500, easing: cubicOut }}" 
  on:mousemove={(event) => autoScrollComponent.handleMouseMove(event)}
  on:wheel|passive={autoScrollComponent.handleMouseWheel}>

    <!-- Navigation -->
    <FontsNav on:toggleGoogleFonts={toggleGoogleFonts} {selectedStorefront}/>
    
    {#if $font_detection_in_progress}
      <LoadingFontsState/>
    {:else}
    <!-- Tags for tagged previews, google fonts and ai categories -->
    {#if 
      !$previews_nav_filters.selected 
      && $user !== undefined 
      || 
      !$previews_nav_filters.selected 
      && $previews_are_google_fonts}
      <PreviewsTags />
    {/if}

    <!-- Business Upsell Notice -->
    {#if 
      $user !== undefined 
      && ($can_upgrade_from_pro_to_business)
      && (($previews_nav_filters.storefront && $user_storefronts.length > 0)
      || ($previews_nav_filters.uploaded && !empty_filter_result))}
      <BusinessUpsellNotice/>
    {/if}

    <!-- Inserts Before Previews -->
    {#if (
      isEmpty($previews_nav_filters) || $previews_nav_filters.user || $previews_are_google_fonts) 
      && 
      isEmpty($previews_search_filter)}
      <InsertManager 
        insert_position='before_previews' 
        selected_previews_count={selected_count} 
        {filtered_preview_count}
        on:autoscroll_start={autoScrollComponent.start}
        {random_insert}/>
    {/if}
    
    <!-- Storefronts Index -->
    {#if 
      $user !== undefined 
      && $previews_nav_filters.storefront 
      && !$previews_storefronts_filter}
      {#if $user_uploaded_fonts_loading_state.loading}
        <div class="loading">
          <Blink>Loading storefronts...</Blink>
        </div>
      {:else if !$user_uploaded_fonts_loading_state.error}
        <StorefrontsIndex storefront_fonts={filtered_previews}/>
      {:else}
        <div class="error">
          <p>
            There was an error loading your uploaded fonts ({$user_uploaded_fonts_loading_state.error}).<br>
            Uploaded fonts are required for storefronts to function.
          </p>
          <Button kind="accent" on:click={handleReloadFonts}>Retry loading</Button>
        </div>
      {/if}
    {:else}

      <!-- View Options -->
      {#if !empty_filter_result}
        <PreviewsViewOptions
          {filtered_preview_count}
          selected_preview_count={selected_count}
          on:deselectAllFonts={deselectAllFonts}
          on:selectAllFonts={selectAllFonts}/>
      {/if}

      <!-- Previews -->
      <div 
        bind:this={previews_element}
        class="previews"  
        class:prepare_for_export
        class:export_without_bg
        class:export_without_fontname
        class:dense = {$previews_font_size < 2}
        class:storefrontPreview = {!!$previews_storefronts_filter}
        class:light_theme = {export_with_inverted_theme && $dark_theme || !export_with_inverted_theme && !$dark_theme}
        class:dark_theme = {export_with_inverted_theme && !$dark_theme || !export_with_inverted_theme && $dark_theme}
        >

        {#if !!$previews_storefronts_filter && $selectedStorefront}
          <StorefrontURLBar {selectedStorefront} storefront_fonts={filtered_previews}/>
          <StorefrontName name={$selectedStorefront.name} on:nameChange={handleStorefrontNameChange}/>
          <StorefrontDescription description={$selectedStorefront.description} on:descriptionChange={handleStorefrontDescriptionChange}/>
          <StorefrontTextfield isPreview={true} text={$selectedStorefront.default_text} on:textChange={handleStorefrontTextfieldTextChange}/>
        {/if}
                
        {#if !empty_filter_result && !prepare_for_export && !$previews_nav_filters.storefront}
          <PreviewsStickyButtons {selected_count} previews_are_dense={$previews_font_size < 2}/>
        {/if}

        <DefaultPreviewWidth/>

        {#if $previews_nav_filters.selected}
          {#each filtered_previews as font (font.id)}
            <Preview {font} on:updateFont={updateFont}/>
          {/each}
        {:else}
          {#each paginated_previews as font, index (font.id)}
            <Preview {font} on:updateFont={updateFont} />
            {#if index % pagination_batch_size === 0 && index >= pagination_batch_size}
              <!-- Insert Insert -->
              {#if !$previews_are_google_fonts}
                <InsertManager 
                  insert_position='between_previews' 
                  inbetween_counter={index/pagination_batch_size} 
                  selected_previews_count={selected_count}
                  {random_insert}/>
              {/if}
            {/if}
          {/each}
          {#if pagination_page * pagination_batch_size < size(filtered_previews)}
            <div class="moreFontsButton">
              <Button kind="outline" on:click={() => {loadNextBatch(); loadNextBatch(); }}>
                {$_('buttons.loadMore')}
              </Button>
            </div>
          {:else if show_font_limit_insert}
            <FontLimitInsert/>
          {:else}
            <InsertManager 
              insert_position='after_previews' 
              selected_previews_count={selected_count}
              {random_insert}/>
          {/if}
        {/if}

        <!-- Scrollmark for pagination -->
        <Inview
          rootMargin="300px"
          wrapper={pagination_scrollmark_el}
          on:enter={loadNextBatch}>
          <div bind:this={pagination_scrollmark_el}></div>
        </Inview>

        <!-- Empty State Controller -->
        {#if empty_filter_result}
          <PreviewsEmptyStateController />
        {/if}

        <!-- Dropzone for Dropped Fonts -->
        {#if $previews_nav_filters.dropped && $dropped_font_files.length > 0}
          <div class="dropzoneAtEnd">
            <Dropzone inputOnly>Load more fonts from your computer</Dropzone> or drag and drop them here
          </div>
        {/if}

        <!-- Dropzone for Uploaded Fonts -->
        {#if $previews_nav_filters.uploaded && !empty_filter_result}
          <div class="dropzoneAtEnd">
            <Dropzone inputOnly on:uploading={handleUploading}>Upload more fonts from your computer</Dropzone>{!uploading ? ' or drag and drop them here' : ''}
          </div>
        {/if}

        <!-- Dropzone for Storefront Fonts -->
        {#if $previews_nav_filters.storefront && !!$previews_storefronts_filter && !empty_filter_result}
          <div class="dropzoneAtEnd">
            <Dropzone inputOnly filesOnly on:uploading={handleUploading}>Add font files from your computer</Dropzone>{!uploading ? ' or drag and drop them here' : ''}
          </div>
        {/if}

      </div>

      {#if $previews_nav_filters.storefront && $previews_search_filter === '' && !!$previews_storefronts_filter}
        <StorefrontFooter />
      {/if}

    {/if}
    
    {#if $previews_nav_filters.selected && !$previews_are_google_fonts}
      <InsertManager insert_position='filtered_previews' {random_insert}/>
    {/if}
  {/if}
  </div>
  <PreviewsScrollToTopButton/>
{/if}
  
<ModalExportPNG 
  element_to_export={$previews_are_zoomed ? document.getElementsByClassName('zoomed')[0] : previews_element} 
  bind:prepare_for_export 
  bind:export_without_bg 
  bind:export_without_fontname
  bind:export_with_inverted_theme
/>
<ModalPrint/>
<ModalBuy on:closeGoogleFonts={closeGoogleFonts} on:removeDroppedFonts={removeDroppedFonts}/>
<ModalManageSavedSelections on:loadSelection={loadSelection} has_selected_fonts={selected_count > 0}/>
<PreviewsScrollObserver {previews_element}/>
<AutoScrollController bind:this={autoScrollComponent}/>
<style>
  .previewsContainer {
    min-height: calc(100% - 8rem);
    background-color: var(--c-bg);
    transition: background-color var(--transition-props);
    color: var(--c-text);
  }
  .previews {
    position: relative;
    padding: 1rem;
    display: flex;
    flex-flow: row wrap;
    text-align: center;
    font-size: 20px; /*Base font size for previews*/
    background-color: var(--c-bg);
    transition: background-color var(--transition-props);
  }
  .previews.dense {
    padding: 1.5rem;
  }
  .prepare_for_export,
  .prepare_for_export :global(*) {
    transition: none !important;
  }
  .prepare_for_export.export_without_bg,
  .prepare_for_export.export_without_bg :global(*) {
    background-color: transparent;
    border-color: transparent;
  }
  .prepare_for_export.export_without_bg.light_theme :global(.preview) {
    color: black !important; 
  }
  .prepare_for_export.export_without_bg.dark_theme :global(.preview) {
    color: white !important;
  }
  .prepare_for_export.export_without_fontname :global(.fontname) {
    visibility: hidden;
  }
  .prepare_for_export :global(.preview.zoomed) {
    border-color: transparent;
  }

  .previews.storefrontPreview {
    border: 5px solid var(--c-text-faded-big);
    border-radius: 1rem;
    margin: 2rem;
    padding: 0 1rem 3rem;
  }

  .moreFontsButton {
    width: 100%;
    margin: 1rem;
    z-index: 1;
  }

  .dropzoneAtEnd {
    width: 100%;
    padding: 1rem;
    text-align: left;
    font-size: 1.6rem;
  }

  :global(.previews.print_clone) {
    padding: 0;
    background-color: #FFFFFF;
    margin: 0 auto;
    page-break-inside: avoid;
  }
  :global(.previews.print_clone *) {
    transition: none !important;
  }
  :global(.previews.print_clone .stickyWrapper) {
    display: none;
  }
  .loading {
    padding: 2rem;
  }
  .error {
    padding: 2rem;
  }
  .error p {
    color: var(--c-danger);
    margin-bottom: 2rem;
  }
</style>

